/**
 * MeasurementEquipment List Page
 * List and manage measurement equipment
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as CompanyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Public as GlobalIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { measurementEquipmentService } from '../../services/measurementEquipmentService';
import { occupationalRiskService } from '../../services/occupationalRiskService';
import { OccupationalRiskStatus } from '../../types/occupationalRisk';
import {
  MeasurementEquipment,
  MeasurementEquipmentFilters,
  MeasurementEquipmentStatus,
} from '../../types/measurementEquipment';
import { ValidityStatusChip } from './components/ValidityStatusChip';
import {
  MeasurementEquipmentFiltersComponent,
  MeasurementFiltersData,
  MEASUREMENT_FILTERS_DEFAULT,
} from './components/MeasurementEquipmentFilters';

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const columns: DataTableColumn<MeasurementEquipment>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (equipment) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={equipment.isGlobal ? 'Equipamento Global' : 'Equipamento da Empresa'}>
          {equipment.isGlobal ? (
            <GlobalIcon fontSize="small" color="primary" />
          ) : (
            <CompanyIcon fontSize="small" color="action" />
          )}
        </Tooltip>
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {equipment.name}
          </Typography>
          {equipment.serialNumber && (
            <Typography variant="caption" color="text.secondary">
              S/N: {equipment.serialNumber}
            </Typography>
          )}
        </Box>
      </Box>
    ),
  },
  {
    id: 'manufacturer',
    label: 'Fabricante / Modelo',
    render: (equipment) => (
      <>
        <Typography variant="body2">{equipment.manufacturer}</Typography>
        <Typography variant="caption" color="text.secondary">
          {equipment.model}
        </Typography>
      </>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (equipment) =>
      equipment.isGlobal ? (
        <Chip label="Global" size="small" color="primary" variant="outlined" />
      ) : (
        <Typography variant="body2">{equipment.companyName ?? '-'}</Typography>
      ),
  },
  {
    id: 'occupationalRisks',
    label: 'Riscos',
    render: (equipment) =>
      !equipment.occupationalRisks || equipment.occupationalRisks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {equipment.occupationalRisks.slice(0, 3).map((r) => (
            <Chip key={r.id} label={r.riskFactorName} size="small" variant="outlined" />
          ))}
          {equipment.occupationalRisks.length > 3 && (
            <Tooltip
              title={equipment.occupationalRisks
                .slice(3)
                .map((r) => r.riskFactorName)
                .join(', ')}
            >
              <Chip label={`+${equipment.occupationalRisks.length - 3}`} size="small" />
            </Tooltip>
          )}
        </Stack>
      ),
  },
  {
    id: 'calibrationDate',
    label: 'Calibração',
    align: 'center',
    render: (equipment) => (
      <Typography variant="body2">{formatDate(equipment.calibrationDate)}</Typography>
    ),
  },
  {
    id: 'validityDate',
    label: 'Validade',
    align: 'center',
    render: (equipment) => (
      <Typography variant="body2">{formatDate(equipment.validityDate)}</Typography>
    ),
  },
  {
    id: 'validityStatus',
    label: 'Status Validade',
    align: 'center',
    render: (equipment) => (
      <ValidityStatusChip
        status={equipment.validityStatus}
        daysUntilExpiry={equipment.daysUntilExpiry}
      />
    ),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (equipment) => (
      <Chip
        label={equipment.status === MeasurementEquipmentStatus.ACTIVE ? 'Ativo' : 'Inativo'}
        color={equipment.status === MeasurementEquipmentStatus.ACTIVE ? 'success' : 'default'}
        size="small"
      />
    ),
  },
];

export const MeasurementEquipmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/measurement-equipments/:id/edit', pode('measurement_equipment.update'), 'Editar');
  const excluir = useAcao('measurement_equipment.delete', 'Excluir');
  const [equipments, setEquipments] = useState<MeasurementEquipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<MeasurementEquipmentFilters>({});
  const [risks, setRisks] = useState<Array<{ id: number; riskFactorName: string; riskType: string }>>([]);

  const loadEquipments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      };

      const response = await measurementEquipmentService.getMeasurementEquipments(params);
      setEquipments(response.equipments);
      setTotal(response.total);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar equipamentos de medição';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadEquipments();
  }, [loadEquipments]);

  useEffect(() => {
    const loadRisks = async () => {
      try {
        const res = await occupationalRiskService.getOccupationalRisks({
          limit: 1000,
          status: OccupationalRiskStatus.ACTIVE,
        });
        setRisks((res.risks ?? []).map(r => ({ id: r.id, riskFactorName: r.riskFactorName, riskType: r.riskType })));
      } catch (err: unknown) {
        console.error('Error loading risks for filter:', err instanceof Error ? err.message : err);
      }
    };
    loadRisks();
  }, []);

  const handleFilter = useCallback((data: MeasurementFiltersData) => {
    const filters: MeasurementEquipmentFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.companyId) filters.companyId = Number(data.companyId);
    if (data.occupationalRiskIds && data.occupationalRiskIds.length > 0) {
      filters.occupationalRiskIds = data.occupationalRiskIds;
    }
    if (data.validityStatus) filters.validityStatus = data.validityStatus as MeasurementEquipmentFilters['validityStatus'];
    if (data.status) filters.status = data.status as MeasurementEquipmentFilters['status'];
    if (data.isGlobal) filters.isGlobal = true;

    setApiFilters(filters);
    setPage(0);
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este equipamento de medição?')) {
      return;
    }

    try {
      await measurementEquipmentService.deleteMeasurementEquipment(id);
      loadEquipments();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao excluir equipamento de medição';
      setError(errorMessage);
    }
  };

  const handleEdit = (equipment: MeasurementEquipment) => {
    navigate(`/measurement-equipments/${equipment.id}/edit`);
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Equipamentos de Medição' },
      ]}
      title="Equipamentos de Medição"
      subtitle="Gerencie os equipamentos de medição e calibração"
      actions={
        <>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<WarningIcon />}
            onClick={() => handleFilter({ ...MEASUREMENT_FILTERS_DEFAULT, validityStatus: 'WARNING' })}
          >
            Expirando
          </Button>
          <RouteActionButton
            para="/measurement-equipments/new"
            exige={pode('measurement_equipment.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Equipamento
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <MeasurementEquipmentFiltersComponent onFilter={handleFilter} risks={risks} loading={loading} total={total} />
      </Box>

      <DataTable
        columns={columns}
        rows={equipments}
        getRowId={(equipment) => equipment.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum equipamento de medição encontrado',
          description:
            'Equipamentos de medição são registrados nesta tela e identificam, em cada medição ambiental, o aparelho usado e sua calibração.',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            color: 'primary',
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (equipment) => handleDelete(equipment.id),
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />
    </PageShell>
  );
};

export default MeasurementEquipmentListPage;
